import {lazy, Suspense} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useDomReady} from '../../../lib/utils/platform'

import {ContentfulHeroLayout} from './ContentfulHeroLayout'
import type {NinetailedExperienceEntry} from '../../../lib/utils/contentful-ninetailed'
import type {ContentfulHeroProps} from './contentful-hero.types'

// Lazy load Ninetailed experience orchestrator to prevent SSR bundling (same pattern as IntroHero)
const ContentfulHeroExperiences = lazy(() => import('./ContentfulHeroExperiences'))

export function ContentfulHero({component, paddingBlockEnd, analyticsContext, ...props}: ContentfulHeroProps) {
  // Check for Ninetailed experiences - only if feature flag is enabled
  const isNinetailedEnabled = isFeatureEnabled('landing_pages_ninetailed')
  const isDomReady = useDomReady()

  // Extract experiences (empty array if not enabled or not present)
  const rawExperiences: NinetailedExperienceEntry[] =
    isNinetailedEnabled && Array.isArray(component.fields.nt_experiences) ? component.fields.nt_experiences : []

  // Baseline component - used for SSR and when no experiences exist
  const baselineLayout = (
    <ContentfulHeroLayout
      component={component}
      paddingBlockEnd={paddingBlockEnd}
      analyticsContext={analyticsContext}
      {...props}
    />
  )

  // If no experiences, just render baseline directly (works for both SSR and client)
  if (rawExperiences.length === 0) {
    return baselineLayout
  }

  // Invisible baseline: reserves the hero's natural dimensions without
  // showing content, preventing both CLS and a flash of baseline before
  // Ninetailed resolves the winning variant.
  const hiddenBaseline = (
    <div className="v-hidden" data-testid="hero-hidden-baseline">
      {baselineLayout}
    </div>
  )

  // During SSR or before DOM is ready, render the hidden baseline so the
  // layout occupies the correct space without any visible content.
  if (!isDomReady) {
    return hiddenBaseline
  }

  // With experiences and DOM ready, lazy load the Ninetailed orchestrator.
  // The hidden baseline continues to reserve space while the chunk loads.
  // ContentfulHeroExperiences applies heroFadeIn to reveal the resolved content.
  return (
    <Suspense fallback={hiddenBaseline}>
      <ContentfulHeroExperiences
        component={component}
        paddingBlockEnd={paddingBlockEnd}
        analyticsContext={analyticsContext}
        rawExperiences={rawExperiences}
        {...props}
      />
    </Suspense>
  )
}
